import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { Box } from '@mui/material'

import { BI_ROUTES } from '@constants'
import { isLoadingCommonFilters, resetCommonFilters } from '@signals'
import { AppStartHint, searchStartHintProps } from '@components'
import { BIActiveGoal, BIAsked, BIChartData, BIGoalsOpen, BIHeaders, BIOptionLabels, BISelectedOffice, BISelectedOfficeOptions, BITableData, clearBISignals, setFormData } from './@signals'
import BIQuerySentence from './QuerySentence'
import BISearchFailed from './SearchFailed'
import PageWrapper from 'Pages/@PageWrapper'
import BIFilters from './Filters'
import BIPageContent from './Content'
import BIPageWrapperActions from 'Pages/BusinessLogistics/BI/Page/Actions'
import VirtualTourButton, { TourOverlay, TOUR_STEPS } from './VirtualTour'
import BIDemoData from './VirtualTour/DemoData'
import { BISchema } from './schema'
import { shouldAutoRun } from './@services/autoRun'
import { decodeSp } from '@utils/spParam'
import { readGoalsOpen } from './@services/goals'
import { goalsUid } from './@hooks/useGoals'


export default function BIPage() {
	const [tourActive, setTourActive] = useState(false)
	// The hint is for a page nobody has searched yet; after an empty search the views say so themselves.
	const hasSearched = useRef(false)
	if (isLoadingCommonFilters.value) hasSearched.current = true
	const hasResults = !!BITableData.value.rowValues?.length || !!BIChartData.value.chartData || !!BIHeaders.value?.length
	const [searchParams] = useSearchParams()

	useEffect(() => {
		resetCommonFilters()
		clearBISignals()
		BISelectedOfficeOptions.value = []
		BISelectedOffice.value = null
		BIAsked.value = null
		BIOptionLabels.value = {}
		BIActiveGoal.value = null

		const decoded = decodeSp(searchParams.get('sp'))
		// CA 1-2, 7: the questions first, unless the page arrives with a query (a dashboard, a link, a saved query) or
		// this user went straight to the form last time
		BIGoalsOpen.value = !decoded && readGoalsOpen(localStorage, goalsUid())
		if (decoded) {
			try {
				setFormData({ ...decoded, urlId: searchParams.get('urlId') })
				// `?run=1` (a BI dashboard's link): search at once when the form is complete (CU-869fmhmx8 D9)
				if (shouldAutoRun(searchParams, decoded, BISchema)) isLoadingCommonFilters.value = true
			} catch {}
		}

		return () => {
			resetCommonFilters()
			clearBISignals()
			BISelectedOfficeOptions.value = []
			BISelectedOffice.value = null
			BIAsked.value = null
			BIActiveGoal.value = null
			BIGoalsOpen.value = false
		}
	}, [])

	return <PageWrapper
		head={`DS Rentacar | ${BI_ROUTES.TITLE}`}
		title={BI_ROUTES.TITLE}
		subtitle={BI_ROUTES.SUBTITLE}
		isEmpty={false}
		filters={<BIFilters/>}
		actions={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<BIPageWrapperActions/>
			<VirtualTourButton onClick={() => setTourActive(true)} />
		</Box>}
	>
		<BIQuerySentence/>
		{!hasSearched.current && !hasResults && !tourActive && !BIGoalsOpen.value && <AppStartHint {...searchStartHintProps} description="The sentence above says what you are about to ask"/>}
		<BISearchFailed/>
		<BIPageContent/>
		{tourActive && <BIDemoData />}
		{tourActive && createPortal(
			<TourOverlay steps={TOUR_STEPS} onClose={() => setTourActive(false)} />,
			document.body
		)}
	</PageWrapper>
}
